import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { availabilityAPI } from '../services/availabilityAPI';
import { Card, Button, PageHeader, Skeleton } from '../components/ui';
import { Calendar, User, Zap, Activity, ChevronRight, BarChart3, AlertCircle } from 'lucide-react';

const getLoadStyle = (value) => {
  if (value >= 100) return { bg: 'rgba(255, 107, 138, 0.15)', color: 'var(--accent2)', border: 'var(--accent2)', glow: '0 0 12px rgba(255, 107, 138, 0.4)' };
  if (value >= 75) return { bg: 'rgba(245, 158, 11, 0.15)', color: 'var(--accent4)', border: 'var(--accent4)', glow: '0 0 12px rgba(245, 158, 11, 0.3)' };
  if (value >= 50) return { bg: 'rgba(124, 106, 255, 0.15)', color: 'var(--accent)', border: 'var(--accent)', glow: '0 0 12px rgba(124, 106, 255, 0.3)' };
  if (value >= 25) return { bg: 'rgba(52, 211, 153, 0.15)', color: 'var(--accent3)', border: 'var(--accent3)', glow: '0 0 12px rgba(52, 211, 153, 0.2)' };
  return { bg: 'var(--bg-hover)', color: 'var(--text-dim)', border: 'var(--border-bright)', glow: 'none' };
};

const getLoadLabel = (value) => {
  if (value >= 100) return 'Overloaded';
  if (value >= 75) return 'Heavy';
  if (value >= 50) return 'Moderate';
  if (value >= 25) return 'Light';
  return 'Available';
};

export default function Availability() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await availabilityAPI.get();
        setData(res.data?.data ?? null);
      } catch (err) {
        setError('Could not load availability data.');
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const hasAvailability = Boolean(data && Array.isArray(data.days) && Array.isArray(data.rows) && data.rows.length > 0);
  const details = loading ? Array.from({ length: 3 }) : (data?.details ?? []);

  return (
    <div className="flex flex-col h-full bg-[var(--bg)] text-[var(--text)] animate-in pb-8" style={{ padding: '0 24px' }}>
      <PageHeader
        eyebrow="Resource Planning"
        title="Team Availability"
        subtitle="Monitor individual workloads, forecast capacity, and balance your sprint planning effectively across the team."
        action={
          <Button variant="primary" onClick={() => {}}>
            <Zap size={18} fill="currentColor" />
            <span>Plan Sprint</span>
          </Button>
        }
        style={{
          '--page-header-bg': 'linear-gradient(135deg, #2563eb, #1d4ed8)',
          '--page-header-text': '#fff',
          '--page-header-muted': 'rgba(255,255,255,0.84)',
        }}
      />

      {error && (
        <div className="mb-6 p-4 rounded-xl flex items-center gap-3 border border-red-500/30 bg-red-500/10 text-red-400">
          <AlertCircle size={20} />
          <span className="font-medium">{error}</span>
        </div>
      )}

      {/* Main Heatmap Matrix */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl shadow-[var(--shadow-card)] mb-8 overflow-hidden relative">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[var(--accent)] via-[var(--accent3)] to-[var(--accent2)]"></div>
        
        <div className="p-6 border-b border-[var(--border)] flex justify-between items-center bg-[var(--bg)] bg-opacity-30">
          <div>
            <h2 className="text-xl font-display font-bold text-[var(--text)]">Capacity Matrix</h2>
            <p className="text-sm text-[var(--text-muted)] mt-1">Current Sprint (Sprint 47)</p>
          </div>
          
          {/* Legend */}
          <div className="flex items-center gap-4 bg-[var(--bg-elevated)] px-4 py-2 rounded-full border border-[var(--border)]">
            <span className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mr-2">Load</span>
            {[0, 25, 50, 75, 100].map((val) => {
              const style = getLoadStyle(val);
              return (
                <div key={val} className="flex items-center gap-1.5" title={getLoadLabel(val)}>
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: val === 0 ? 'var(--bg-hover)' : style.color, boxShadow: style.glow, border: val === 0 ? '1px solid var(--border-bright)' : 'none' }}></div>
                  <span className="text-[11px] font-bold text-[var(--text-dim)]">{val}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="p-6">
            <Skeleton height={280} style={{ borderRadius: '12px' }} />
          </div>
        ) : !hasAvailability ? (
          <div className="p-16 text-center flex flex-col items-center">
            <div className="w-16 h-16 rounded-full bg-[var(--bg-hover)] flex items-center justify-center mb-4">
              <Calendar size={28} className="text-[var(--text-dim)]" />
            </div>
            <h3 className="text-lg font-bold text-[var(--text)] mb-2">No active schedule</h3>
            <p className="text-[var(--text-muted)]">There is no availability data to display for this period.</p>
          </div>
        ) : (
          <div className="overflow-x-auto custom-scrollbar p-6">
            <div className="min-w-max">
              {/* Table Header */}
              <div className="flex mb-4">
                <div className="w-64 shrink-0 px-4 text-[13px] font-bold text-[var(--text-muted)] uppercase tracking-widest">Team Member</div>
                <div className="flex flex-1 gap-2">
                  {data.days.map((day, i) => {
                    const [date, dayName] = day.split(' ');
                    return (
                      <div key={i} className="flex-1 min-w-[80px] flex flex-col items-center justify-center">
                        <span className="text-[11px] font-bold text-[var(--text-dim)] uppercase tracking-widest">{dayName}</span>
                        <span className="text-[15px] font-display font-bold text-[var(--text)] mt-0.5">{date}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Table Rows */}
              <div className="flex flex-col gap-3">
                {data.rows.map((row) => (
                  <div key={row.user_id} className="flex items-center bg-[var(--bg-elevated)] border border-[var(--border)] rounded-xl hover:border-[var(--border-bright)] transition-colors group">
                    <div className="w-64 shrink-0 p-4 flex items-center gap-4 border-r border-[var(--border)] group-hover:bg-[rgba(255,255,255,0.02)] transition-colors rounded-l-xl">
                      <div className="w-10 h-10 rounded-full bg-[var(--bg-hover)] border border-[var(--border-bright)] flex items-center justify-center text-[13px] font-bold text-[var(--text)] shadow-sm">
                        {row.initials}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[15px] font-bold text-[var(--text-primary)] leading-tight">{row.name}</span>
                        <span className="text-[12px] text-[var(--text-muted)] font-medium mt-0.5">{row.role}</span>
                      </div>
                    </div>
                    <div className="flex flex-1 gap-2 p-3">
                      {row.values.map((value, index) => {
                        const style = getLoadStyle(value || 0);
                        const isNull = value === null;
                        return (
                          <div key={index} className="flex-1 min-w-[80px] flex items-center justify-center">
                            <div 
                              className="w-full h-11 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-105 cursor-pointer"
                              style={{ 
                                backgroundColor: isNull ? 'transparent' : style.bg,
                                border: `1px solid ${isNull ? 'transparent' : style.border}`,
                                color: isNull ? 'var(--text-dim)' : style.color,
                                boxShadow: isNull ? 'none' : style.glow
                              }}
                              title={isNull ? 'No Data' : `${value}% - ${getLoadLabel(value)}`}
                            >
                              <span className="text-[14px] font-bold font-display tracking-wide">
                                {isNull ? '—' : `${value}%`}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Individual Detail Cards */}
      <div className="mb-6 flex items-center gap-2">
        <BarChart3 size={18} className="text-[var(--text-muted)]" />
        <h3 className="text-lg font-display font-bold text-[var(--text)]">Individual Breakdown</h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {details.map((detail, index) => (
          <div key={index} className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl p-6 shadow-[var(--shadow-card)] hover:border-[var(--accent-glow)] transition-all duration-300 relative overflow-hidden group">
            {detail ? (
              <>
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-[var(--accent)] opacity-5 rounded-full blur-2xl group-hover:opacity-10 transition-opacity"></div>
                
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[var(--bg-elevated)] border border-[var(--border-bright)] flex items-center justify-center text-[15px] font-bold text-[var(--text)] shadow-sm relative z-10">
                      {detail.initials}
                    </div>
                    <div>
                      <h4 className="text-[16px] font-bold text-[var(--text-primary)]">{detail.name}</h4>
                      <p className="text-[13px] text-[var(--accent)] font-semibold mt-0.5">{detail.role}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => navigate(`/app/users/${detail.user_id}`)}
                    className="w-8 h-8 rounded-full bg-[var(--bg-hover)] flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text)] hover:bg-[var(--border)] transition-colors"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>

                <div className="space-y-4">
                  {detail.daily.map((day, i) => {
                    const style = getLoadStyle(day.percent || 0);
                    const isNull = day.percent === null;
                    return (
                      <div key={i} className="flex items-center gap-4 group/row">
                        <div className="w-14 shrink-0 text-[12px] font-bold text-[var(--text-muted)] tracking-wider">
                          {day.day}
                        </div>
                        <div className="flex-1 h-2.5 bg-[var(--bg-hover)] rounded-full overflow-hidden border border-[var(--border)] relative">
                          <div 
                            className="absolute top-0 left-0 h-full rounded-full transition-all duration-700 ease-out"
                            style={{ 
                              width: `${day.percent || 0}%`, 
                              backgroundColor: style.color,
                              boxShadow: style.glow
                            }}
                          ></div>
                        </div>
                        <div className="w-12 text-right text-[13px] font-display font-bold" style={{ color: isNull ? 'var(--text-dim)' : style.color }}>
                          {isNull ? '—' : `${day.percent}%`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <Skeleton height={240} style={{ borderRadius: '12px' }} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
